import { autoinject, PLATFORM, useView } from 'aurelia-framework';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { AcurityTable } from '~/components/acurity-table/acurity-table';
import { FilterType } from '~/components/filter/filter-options';
import ClassicForm from '~/forms/classic-form';
import { HiddenTarget } from '~/models/column-definition';
import { DataTypeEnum } from '~/models/data-type';
import { createFilterModel } from '~/models/filter-model';
import { PersonalDetailsField } from '~/models/generated/entities/PersonalDetails';
import { computedFrom } from 'aurelia-binding';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import { AppRouteID } from '~/app-routes';
import { CommonURLParam } from '~/constants';
import { generateLinkNoCustomisationParam } from '~/utils/router-utils';

@autoinject
@useView(PLATFORM.moduleName('~/forms/classic-form.html'))
export class PersonalDetails extends ClassicForm {
	public static readonly FORM_NAME = 'personal-details';
	public static readonly ENABLE_AUDIT_FIELD_GROUPS =
		'ENABLE_AUDIT_FIELD_GROUPS';

	private params: { [key: string]: string };

	private deidentifiedFieldValues: string[];

	private isClientAddressAuditEnabled: boolean = false;

	constructor(...rest) {
		super(
			PersonalDetails.FORM_NAME,
			true, // save
			false, // delete
			true, // notes
			true, // audit
			false, // add
			2,
			['D2z_Client'],
			...rest,
		);
		this.addCustomisableAreaName = 'singleRecordDisplay';
		this.tableConfig.rowIdKey = 'D2i_Identity';
		this.tableConfig.defaultSortField = 'D2i_Identity';
		this.tableConfig.defaultSortDirection = 'asc';
		this.tableConfig.tableColumns = [
			{
				name: 'D2z_Client',
				label: 'Client',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'D2z_Title',
				label: 'Title',
				dataType: { name: DataTypeEnum.String },
				isDisableColumnDescription: true,
			},
			{
				name: 'D2z_Surname',
				label: 'Surname',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'D2z_Given_Names',
				label: 'Given Names',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'D2d_Birth',
				label: 'Date of Birth',
				dataType: { name: DataTypeEnum.Date },
			},
			{
				name: PersonalDetailsField.D2c_Deidentified,
				label: 'Deidentified',
				dataType: { name: DataTypeEnum.String },
				hiddenFrom: [HiddenTarget.TABLE, HiddenTarget.EXPORT],
			},
		];
		this.tableConfig.multiColumnOrder = [
			{ name: 'D2z_Client', direction: 'asc' },
			{ name: 'D2z_Title', direction: 'asc' },
			{ name: 'D2z_Surname', direction: 'asc' },
			{ name: 'D2z_Given_Names', direction: 'desc' },
		];
		this.tableConfig.selectIfOnlyOne = true;
	}

	/**
	 * @override
	 */
	protected activate(
		params: { [param: string]: any },
		routeConfig: RouteConfig,
		navigationInstruction: NavigationInstruction,
	): Promise<any> {
		const parentPromise: Promise<any> = super.activate(
			params,
			routeConfig,
			navigationInstruction,
		);
		if (params[PersonalDetailsField.D2c_Deidentified]) {
			this.deidentifiedFieldValues =
				params[PersonalDetailsField.D2c_Deidentified].split(',');
		}

		const clientAddressAuditPromise = this.classicFormsApi
			.getConfigValue(PersonalDetails.ENABLE_AUDIT_FIELD_GROUPS)
			.then((configKeyValue) => {
				this.isClientAddressAuditEnabled =
					configKeyValue.value as boolean;
			});

		return Promise.all([parentPromise, clientAddressAuditPromise]);
	}

	/**
	 * @override
	 */
	protected attached() {
		super.attached();
		if (!this.deidentifiedFieldValues) {
			return;
		}

		this.subscribeToEvent(
			AcurityTable.Events.Attached,
			(data: { table: AcurityTable }) => {
				if (data.table === this.recordSelectorTable) {
					this.tableConfig.tableColumns.find(
						(column) =>
							column.name ===
							PersonalDetailsField.D2c_Deidentified,
					).filter = createFilterModel({
						operation: {
							value: FilterType.IN,
							displayName: 'filter.option.is-in',
							dataTypeNames: [DataTypeEnum.String],
						},
						value: this.deidentifiedFieldValues,
					});
				}
			},
		);
	}

	@computedFrom('formModel')
	public get slideoutContextMenu(): ContextMenuItem[] {
		if (!this.formModel) {
			return [];
		}

		const commonContextMenuItems = this.getCommonContextMenuItems();
		if (this.isClientAddressAuditEnabled) {
			commonContextMenuItems.push(
				this.getAddressAuditDetailsContextMenuItem(),
			);
		}
		return commonContextMenuItems;
	}

	/**
	 * @override
	 */
	@computedFrom(
		'formModel',
		'contextMenuItemsClassicForm',
		'isClientAddressAuditEnabled',
	)
	protected get contextMenuItems(): ContextMenuItem[] {
		if (!this.formModel) {
			return [];
		}
		const viewItemIndex = this.contextMenuItemsClassicForm.findIndex(
			(v) => v.id === this.viewMenuId,
		);
		if (viewItemIndex === -1) {
			throw new Error('View context menu item is missing');
		}

		const allItemsExceptView = [...this.contextMenuItemsClassicForm];
		const viewItem = allItemsExceptView.splice(viewItemIndex, 1)[0];

		if (this.isClientAddressAuditEnabled) {
			allItemsExceptView.push(
				this.getAddressAuditDetailsContextMenuItem(),
			);
		}

		return [
			viewItem,
			...this.getCommonContextMenuItems(),
			...allItemsExceptView,
		];
	}

	private getAddressAuditDetailsContextMenuItem(): ContextMenuItem {
		return {
			id: this.auditDetailsMenuId,
			label: this.i18n.tr('personal-details:address-audit-details'),
			iconClass: 'fa-history',
			disabled: !this.slideOut.canOpenAuditDetails,
			onClick: () => this.contextAddressAuditDetails(),
			tooltip: '{{label}}',
		};
	}

	private contextAddressAuditDetails(): void {
		if (!this.updateRowAndCanDoContextMenuAction()) {
			return;
		}
		this.slideOut.showAddressAuditDialog();
	}

	private getCommonContextMenuItems(): ContextMenuItem[] {
		const params: { [key: string]: string } = { ...this.params };

		if (this.formModel && this.formModel.fields) {
			params[CommonURLParam.CLIENT] =
				this.formModel.fields.D2z_Client.value;
		}

		const commonContextMenuItems: ContextMenuItem[] = [];
		if (
			this.formModel.fields[PersonalDetailsField.D2c_Deidentified]
				.value === 'D'
		) {
			commonContextMenuItems.push({
				label: this.i18n.tr(
					'common-strings:route-title:deidentified-clients',
				),
				iconClass: 'fa-external-link-alt',
				href: generateLinkNoCustomisationParam(
					AppRouteID.DeidentifiedClients,
					params,
				),
				tooltip: '{{label}}',
			});
		}

		return commonContextMenuItems;
	}
}
